import type { TicketStatus, Resolution } from '@lixionary-jira/domain';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { ApiError, viewerApi } from '../../api/client';
import { Markdown } from '../../ui/Markdown';
import { StatusBadge, humanize } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Card, LoadMore, usePageTitle } from '../../ui/misc';
import { ErrorAlert, PageLoading, QueryView } from '../../ui/feedback';
import { Timestamp } from '../../ui/Timestamp';

export interface ViewerTicket {
  key: string;
  title: string;
  type: string;
  status: TicketStatus;
  resolution: Resolution | null;
  priority: string;
  project: { key: string; name: string };
  description: string;
  acceptanceCriteria: string[];
  stepsOrOutcome: string;
  updatedAt: string;
  csrfToken: string;
  attachments: {
    id: string;
    filename: string;
    detectedType: string;
    sizeBytes: number;
    commentId: string | null;
  }[];
}
type ViewerComment = { id: string; author: string; body: string | null; deleted: boolean; createdAt: string };
export function ViewerExpiredPage() {
  return (
    <main className="page narrow">
      <h1 className="page-title">Ticket window unavailable</h1>
      <p>
        This window has expired or access has changed. Close it and open the ticket again from your
        application.
      </p>
      <Button onClick={() => window.close()}>Close window</Button>
    </main>
  );
}
function expired(error: unknown) {
  return error instanceof ApiError && [401, 403, 404].includes(error.status);
}

/** Deliberately small read-only projection. Never renders the member workspace or its action controls. */
export function TicketViewerPage() {
  const { ticketKey } = useParams();
  const base = `/tickets/${encodeURIComponent(ticketKey ?? '')}`;
  const q = useQuery({
    queryKey: ['ticket-viewer', ticketKey],
    queryFn: () => viewerApi.get<ViewerTicket>(base),
    retry: false,
    refetchOnWindowFocus: true,
  });
  const comments = useInfiniteQuery({
    queryKey: ['ticket-viewer-comments', ticketKey],
    enabled: !!q.data && !q.error,
    queryFn: ({ pageParam }) =>
      viewerApi.get<{ items: ViewerComment[]; nextCursor: string | null }>(
        `${base}/comments${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false,
  });
  usePageTitle(q.data ? `${q.data.key} · ${q.data.title}` : 'Ticket details');
  useEffect(() => {
    if (q.data) viewerApi.setCsrfToken(q.data.csrfToken);
  }, [q.data]);
  if (expired(q.error) || expired(comments.error)) return <ViewerExpiredPage />;
  const files = (t: ViewerTicket, commentId: string | null) =>
    t.attachments
      .filter((a) => a.commentId === commentId)
      .map((a) => (
        <div key={a.id} className="stack sm viewer-file">
          {a.detectedType.startsWith('image/') && (
            <img alt={a.filename} loading="lazy" src={`/api/viewer/v1${base}/attachments/${a.id}/preview`} />
          )}
          {a.detectedType.startsWith('video/') && (
            <video
              controls
              preload="none"
              aria-label={a.filename}
              src={`/api/viewer/v1${base}/attachments/${a.id}/preview`}
            />
          )}
          <a href={`/api/viewer/v1${base}/attachments/${a.id}/download`} download>
            {a.filename}
          </a>
        </div>
      ));
  return (
    <main className="page narrow ticket-viewer">
      <header className="row between">
        <span className="small muted">Ticket details · read only</span>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void viewerApi
              .post('/session/end')
              .catch(() => undefined)
              .finally(() => window.close());
          }}
        >
          Close window
        </Button>
      </header>
      <QueryView query={q}>
        {(t) => (
          <>
            <div className="stack sm">
              <span className="mono">
                {t.key} · {t.project.name}
              </span>
              <h1 className="page-title">{t.title}</h1>
              <div className="row">
                <StatusBadge status={t.status} resolution={t.resolution} />
                <span>
                  {humanize(t.type)} · {humanize(t.priority)} priority
                </span>
              </div>
              <span className="small muted">
                Updated <Timestamp value={t.updatedAt} />
              </span>
            </div>
            <Card title="Description">
              {t.description.trim() ? (
                <Markdown>{t.description}</Markdown>
              ) : (
                <p className="muted">No description provided.</p>
              )}
            </Card>
            <Card title="Acceptance criteria">
              {t.acceptanceCriteria.length ? (
                <ol className="stack sm">
                  {t.acceptanceCriteria.map((c, i) => (
                    <li key={i}>
                      <Markdown>{c}</Markdown>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="muted">No acceptance criteria provided.</p>
              )}
            </Card>
            {t.stepsOrOutcome && (
              <Card title={t.type === 'issue' ? 'Reproduction steps' : 'Intended outcome'}>
                <Markdown>{t.stepsOrOutcome}</Markdown>
              </Card>
            )}
            {t.attachments.some((a) => !a.commentId) && <Card title="Attachments">{files(t, null)}</Card>}
            <Card title="Discussion">
              {comments.isPending ? (
                <PageLoading />
              ) : comments.error ? (
                <ErrorAlert error={comments.error} />
              ) : (
                <div className="stack lg">
                  {comments.data?.pages
                    .flatMap((p) => p.items)
                    .map((c) => (
                      <article key={c.id} className="stack sm">
                        <div className="row small">
                          <strong>{c.author}</strong>
                          <Timestamp value={c.createdAt} />
                        </div>
                        {c.deleted ? (
                          <p className="muted">Comment removed.</p>
                        ) : (
                          <>
                            <Markdown>{c.body ?? ''}</Markdown>
                            {files(t, c.id)}
                          </>
                        )}
                      </article>
                    ))}
                  {!comments.data?.pages.some((p) => p.items.length) && (
                    <p className="muted">No comments yet.</p>
                  )}
                  <LoadMore
                    hasMore={!!comments.hasNextPage}
                    loading={comments.isFetchingNextPage}
                    onClick={() => void comments.fetchNextPage()}
                  />
                </div>
              )}
            </Card>
          </>
        )}
      </QueryView>
    </main>
  );
}
